import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import React, { useState } from "react";
import { ResolveThunks, connect, useDispatch } from "react-redux";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import ADIRfqQuoteDetailsHeaderSection from "@NA/Components/RfqQuoteDetails/ADIRfqQuoteDetailsHeaderSection";
import ADIRfqQuoteDetailsSummaryTableMobile from "@NA/Components/RfqQuoteDetails/ADIRfqQuoteDetailsSummaryTableMobile";
import ADIRfqQuoteDetailsOrderLinesList from "@NA/Components/RfqQuoteDetails/ADIRfqQuoteDetailsOrderLinesList";
import ADIRfqQuoteDetailsTitleHeader from "@NA/Components/RfqQuoteDetails/ADIRfqQuoteDetailsTitleHeader";
import ADIRfqQuoteDetailsBottomTotalSection from "@NA/Components/RfqQuoteDetails/ADIRfqQuoteDetailsBottomTotalSection";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";
import getQuoteDetailsHandler from "@NA/Handlers/GetQuoteDetailsHandler";
import translate from "@insite/client-framework/Translate";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import updateProjectQuoteHandler from "@NA/Handlers/ADIUpdateProjectQuoteHandler";
import { cloneDeep } from "lodash";
import { RfqQuoteDetailsPageContext } from "@insite/content-library/Pages/RfqQuoteDetailsPage";
import ADIColors from "@NA/Utils/ADIColors";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import ADIXtenExportToXtenButton from "@NA/Components/ADIXtenExportToXtenButton";
import { setOrderType } from "@NA/Utils/Utility";

const mapStateToProps = (state: CustomApplicationState) => {
    const settingsCollection = getSettingsCollection(state);
    const myNavSetting = settingsCollection?.maNavSettingSettings as any;
    const userRoles = state.context.session.userRoles;
    const { search } = getLocation(state);
    const { quotenumber } = parseQueryString(search) as unknown as any;
    const currentCart = getCurrentCartState(state).value;
    const { suffix } = parseQueryString(search) as unknown as any; // P21 Change
    // const customerSuffix = state.ADIGlobalData?.quotesListStore?.quotesList?.properties?.customerSuffix ?? "000";    // Code from Spire Prod
    const quoteDetails = state.ADIGlobalData?.quoteDetailsStore?.data as OrderModel;
    return {
        userRoles,
        quote: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
        quoteDetails,
        quoteDetailsLoading: state.ADIGlobalData.quoteDetailsStore.isLoading,
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
        quoteNumber: quotenumber,
        myNavSetting,
        cart: currentCart,
        customerSuffix: suffix, // In P21 suffix is being used. But in Spire Prod, customerSuffix is being used.
    };
};

const mapDispatchToProps = {
    getQuoteDetailsHandler,
    updateProjectQuoteHandler,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface ADIQuoteDetailsContainerStyles {
    loadingOverlay?: LoadingOverlayProps;
    adiLogo?: InjectableCss;
    container?: GridContainerProps;
    headerGridItem?: GridItemProps;
    firstSection?: GridItemProps;
    secondSection?: GridItemProps;
    thirdSection?: GridItemProps;
    erroMsgSection?: GridItemProps;
}

export const adiQuoteDetailsContainerStyles: ADIQuoteDetailsContainerStyles = {
    loadingOverlay: {
        css: css`
            & > div:first-child[data-loading="true"] {
                position: fixed;
            }
        `,
    },
    adiLogo: {
        css: css`
            img {
                display: none;
                @media print {
                    display: block;
                    width: 100px;
                    height: auto;
                    margin-bottom: 20px;
                    filter: brightness(1) invert(1);
                }
            }
        `,
    },
    headerGridItem: {
        width: 12,
        css: css`
            align-items: center;
            @media only screen and (max-width: 576px) {
                padding-top: 0px;
                width: 100%;
                flex-direction: column;

                .btnStyleWrapper {
                    width: 100%;
                    justify-content: center;
                }
                .btnExportWrapper {
                    width: 100%;
                    margin: 0;
                    margin-top: 20px;
                }
            }
        `,
    },
    erroMsgSection: {
        width: 12,
        css: css`
            background-color: ${getColor("common.background")};
            min-height: auto;
            padding: 0;
            .rd-myquotedetails_maxwd1474 {
                width: 100%;
                .error-msg {
                    &.quote-record {
                        max-width: 100%;
                        padding: 24px 0px 0px;
                        font-size: 14px;
                        font-weight: bold;
                        line-height: 16px;
                        letter-spacing: normal;
                        color: ${ADIColors.black};
                        display: flex;
                        justify-content: center;
                        align-items: flex-start;
                    }
                    img {
                        height: auto;
                        width: 17px;
                        margin-right: 10px;
                    }
                    span {
                        font-family: "Nunito Sans";
                        font-size: 14px;
                        font-weight: bold;
                        font-stretch: normal;
                        font-style: normal;
                        line-height: 16px;
                        letter-spacing: normal;
                        color: #000;
                        a {
                            color: #006ae3;
                            margin-left: 8px;
                            line-height: 14px;
                        }
                        a:hover {
                            text-decoration: underline;
                        }
                    }
                }
            }
        `,
    },
    firstSection: {
        width: 12,
        css: css`
            background-color: ${getColor("common.background")};
            border-bottom: 1px solid ${getColor("text.disabled")};
            padding-bottom: 0;
            @media only screen and (max-width: 1024px) {
                border-bottom: 0;
            }
        `,
    },
    secondSection: {
        width: 12,
        printWidth: 12,
        css: css`
            flex-direction: column;
            background: ${getColor("common.background")};
        `,
    },
    thirdSection: {
        width: 12,
        printWidth: 12,
        css: css`
            background: ${getColor("common.background")};
        `,
    },
};

const styles = adiQuoteDetailsContainerStyles;

const ADIQuoteDetailsContainer: React.FC<Props> = ({
    userRoles,
    quoteDetails,
    quoteDetailsLoading,
    quoteNumber,
    getQuoteDetailsHandler,
    myNavSetting,
    cart,
    history,
    updateProjectQuoteHandler,
    totalCountDisplay,
    customerSuffix,
}) => {
    const [canUserAccess, setCanUserAccess] = useState<boolean>(false);
    const [canUserAcceptAndCheckout, setCanUserAcceptAndCheckout] = useState<boolean>(true);
    const [isNonReturnableModalOpen, setIsNonReturnableModalOpen] = useState(false);
    const [isLogicBrokerModalOpen, setIsLogicBrokerModalOpen] = useState(false);
    const [isCartNotificationModalOpen, setIsCartNotificationModalOpen] = useState(false);
    const [isAcceptCheckoutDisable, setIsAcceptCheckoutDisable] = useState<boolean>(false);
    const [isQuoteUpdating, setIsQuoteUpdating] = useState(false);
    const [qtyItems, setQtyItems] = useState<any>([]);
    const [newQuoteDetails, setNewQuoteDetails] = useState(quoteDetails);
    let [shopmyBranchDetailsLabel, setShopmyBranchDetailsLabel] = useState<string>("");
    const quoteProjectStatus = quoteDetails?.properties["projectStatus"] === "Y";
    const projectNewOrder = quoteDetails?.properties?.projectNewOrder;
    const returnCode = quoteDetails?.properties["returnCode"];
    const orderLinesLength = quoteDetails?.orderLines?.length;
    let ExpireQuoteDays = 0;

    React.useEffect(() => {
        setNewQuoteDetails(quoteDetails);
        const newQtyItems: any = [];
        quoteDetails?.invoiceOrderLines[0]?.invoiceOrderLines.forEach((item: any, index: number) =>
            newQtyItems.push({
                ...item,
                qtyOrdered: item.qtyShipped,
            }),
        );
        setQtyItems(newQtyItems);
    }, [quoteDetails]);

    React.useEffect(() => {
        if (quoteNumber) {
            getQuoteDetailsHandler({
                orderNumber: quoteNumber,
            });
        }
    }, [quoteNumber]);

    React.useEffect(() => {
        if (userRoles) {
            if (userRoles !== "undefined" && userRoles !== null && userRoles !== "") {
                let userRolesData = userRoles.split(",");
                for (let userRole of userRolesData) {
                    if (userRole.trim() === userRoles[0].trim()) {
                        setCanUserAccess(true);
                    }

                    if (userRole.trim().toLowerCase() === "limitedaccess") {
                        setCanUserAcceptAndCheckout(false);
                    }
                }
            }
        }
    }, [userRoles]);

    React.useEffect(() => {
        if (myNavSetting !== null) {
            shopmyBranchDetailsLabel = myNavSetting?.billtoDeafultWareHouseDescription
                ? myNavSetting?.billtoDeafultWareHouseDescription
                : shopmyBranchDetailsLabel;
            setShopmyBranchDetailsLabel(
                shopmyBranchDetailsLabel[0].toUpperCase() + shopmyBranchDetailsLabel?.slice(1).toLowerCase(),
            );
        }
    }, []);

    const abcCodeFilter = newQuoteDetails?.orderLines?.filter(
        item => item?.properties?.abcCode === "N" && item.qtyOrdered > 0,
    ).length;
    const abcCodeFilterTwo = newQuoteDetails?.orderLines?.filter(item => item?.properties?.abcCode === "N").length;

    const orderDate = quoteDetails?.properties["quoteOrderDate"];
    const parsedDate = new Date(
        `${orderDate?.slice(0, 4)}-${orderDate?.slice(4, 6)}-${orderDate?.slice(6, 8)}T00:00:00`,
    );
    const formattedDate = parsedDate
        .toLocaleString("en-US", { month: "short", day: "2-digit", year: "numeric" })
        .replace(" ", ". ");

    // P21 Change
    let expDate = quoteDetails?.properties["expiryDate"];
    // const parsedExpDate = new Date(`${expDate?.slice(4, 8)}-${expDate?.slice(0, 2)}-${expDate?.slice(2, 4)}T00:00:00`);
    // const formattedExpDate = parsedExpDate
    //     .toLocaleString("en-US", { month: "short", day: "2-digit", year: "numeric" })
    //     .replace(" ", ". ");
    // Commented the above code since the we are getting the expected formatted date from quoteDetails itself.

    // P21 Change
    let hostname = window.location.hostname.toString();
    const isfr = hostname.indexOf("fr") > 0;
    if (isfr) {
        expDate = expDate?.replace(". ", " ");
    }

    const acceptCheckout = (url: string, customerOrderId: string) => {
        if (cart?.type == "Quote" || cart?.cartLines?.length === 0) {
            checkNonReturnableProducts(url, customerOrderId);
        } else {
            setIsCartNotificationModalOpen(true);
        }
        sessionStorage.setItem("quoteNumber", newQuoteDetails?.erpOrderNumber);
    };

    const closeModalCartNotification = () => {
        setIsCartNotificationModalOpen(false);
    };

    const closeModalNonReturnable = () => {
        setIsNonReturnableModalOpen(false);
    };

    const closeModalLogicBroker = () => {
        setIsLogicBrokerModalOpen(false);
    };
    const AgreeAndContinueCheckout = (url: string, customerOrderId: string) => {
        if (quoteProjectStatus) {
            continueProjectCheckout(url, customerOrderId);
        } else {
            continueCheckout(url, customerOrderId);
        }
    };

    const checkNonReturnableProducts = (url: string, customerOrderId: string) => {
        if (quoteProjectStatus) {
            if (abcCodeFilter && abcCodeFilter > 0) {
                setIsCartNotificationModalOpen(false);
                setIsNonReturnableModalOpen(true);
            } else {
                continueProjectCheckout(url, customerOrderId);
            }
        } else {
            if (abcCodeFilterTwo && abcCodeFilterTwo > 0) {
                setIsCartNotificationModalOpen(false);
                setIsNonReturnableModalOpen(true);
            } else {
                continueCheckout(url, customerOrderId);
            }
        }
    };

    const continueAndCheckoutFromLB = (url: string, customerOrderId: string): void => {
        setOrderType("quote");
        setIsLogicBrokerModalOpen(false);
        setIsCartNotificationModalOpen(false);
        setIsNonReturnableModalOpen(false);
        url += customerOrderId;
        history.push(url);
    };

    const continueCheckout = (url: string, customerOrderId: string): void => {
        const isQuoteHasAnyLBProduct = newQuoteDetails?.orderLines?.some(
            e => e.properties?.logicBroker?.toLowerCase() === "yes" && Number(e.qtyOrdered) !== 0,
        );
        if (isQuoteHasAnyLBProduct) {
            setIsLogicBrokerModalOpen(true);
            setIsCartNotificationModalOpen(false);
            setIsNonReturnableModalOpen(false);
        } else {
            setOrderType("quote");
            url += customerOrderId;
            history.push(url);
            setIsCartNotificationModalOpen(false);
            setIsNonReturnableModalOpen(false);
        }
    };

    const continueProjectCheckout = (url: string, customerOrderId: string): void => {
        if (isQuoteUpdating) return;
        setIsQuoteUpdating(true);

        let invoiceCloneNew: any = [];
        newQuoteDetails?.invoiceOrderLines[0]?.invoiceOrderLines?.forEach((ol: any) => {
            let invoiceClone = cloneDeep(ol);
            let olp = newQuoteDetails?.orderLines?.filter(ool => ool?.productErpNumber === ol?.productErpNumber)[0];
            invoiceClone.costCode = "";
            invoiceClone.notes = "";
            invoiceClone.status = "";
            invoiceClone.unitOfMeasure = "";
            invoiceClone.id = "00000000-0000-0000-0000-000000000000";
            invoiceClone.erpNumber = ol.productErpNumber;
            invoiceClone.shortDescription = olp?.description;
            invoiceClone.qtyOrdered = olp?.qtyOrdered;
            invoiceClone.properties.productErpNumber = ol.productErpNumber;
            invoiceClone.productId = olp?.productId == null ? "00000000-0000-0000-0000-000000000000" : olp?.productId;
            invoiceCloneNew.push(invoiceClone);
        });

        updateProjectQuoteHandler({
            isProject: quoteProjectStatus ? true : false,
            hasOrderNumber: projectNewOrder === null || projectNewOrder === "" ? false : true,
            cartId: newQuoteDetails?.id,
            name: newQuoteDetails?.properties?.projectName,
            orderNumber: projectNewOrder === null || projectNewOrder === "" ? "PENDING" : projectNewOrder,
            quoteNumber: quoteNumber,
            orderTotal: newQuoteDetails?.orderTotal,
            orderLines: invoiceCloneNew,
            contractUid: newQuoteDetails?.properties?.contractUID, // This property is being used in P21
            isQuoteCompleted: newQuoteDetails?.properties?.isQuoteCompleted,
            quoteLinecollection: newQuoteDetails?.quoteLineItems?.join(",") || "",
            onSuccess: () => {
                continueCheckout(url, customerOrderId);
            },
            onComplete: () => {
                setIsQuoteUpdating(false);
            },
        });
        // continueCheckout(url, customerOrderId);
    };

    // const isExpireQuoteWarning = (date: string) => {
    //     const sanitizedDateStr = date.replace(".", " ");
    //     const expireQuote = new Date(sanitizedDateStr);
    //     const currentDate = new Date();

    //     expireQuote.setHours(0, 0, 0, 0);
    //     currentDate.setHours(0, 0, 0, 0);

    //     const msPerDay = 1000 * 60 * 60 * 24;
    //     const days = Math.floor((expireQuote.getTime() - currentDate.getTime()) / msPerDay); // ⬅️ note the change

    //     if (days < 4 && days >= -1) {
    //         ExpireQuoteDays = days;
    //         return true;
    //     }

    //     return false;
    // };

    const isQuoteExpired = (date: string) => {
        let month = date?.substring(0, 2);
        let day = date?.substring(2, 4);
        let year = date?.substring(4, 8);
        let out = month + "/" + day + "/" + year;
        let expireQuote = new Date(out);
        let currentDate = new Date();
        let days = Math.floor((expireQuote.getTime() - currentDate.getTime()) / 1000 / 60 / 60 / 24);
        if (days < -1) {
            return true;
        }
        return false;
    };

    // const isExpireQuoteError = (date: string) => {
    //     let expireQuote = new Date(date);
    //     let currentDate = new Date();
    //     let days = Math.floor((currentDate.getTime() - expireQuote.getTime()) / 1000 / 60 / 60 / 24);
    //     if (days < 3 && days >= -1) {
    //         ExpireQuoteDays = days + 1;
    //         return true;
    //     }
    //     return false;
    // };

    const isExpireQuoteError = (date: string) => {
        const sanitizedDateStr = date?.replace(".", " ");
        const expireQuote = new Date(sanitizedDateStr);
        const currentDate = new Date();

        expireQuote.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);

        const msPerDay = 1000 * 60 * 60 * 24;
        const days = Math.floor((expireQuote.getTime() - currentDate.getTime()) / msPerDay); // ⬅️ note the change

        if (days < 4 && days >= -1) {
            ExpireQuoteDays = days;
            return true;
        }

        return false;
    };

    // let isQuoteExpiredFlag = isQuoteExpired(quoteDetails?.properties["expiryDate"]);
    let isQuoteExpiredFlag = false;
    const currentDate = new Date();
    // const quoteExpired = (expiryDate: any) => {
    //     const expiry = new Date(expiryDate);
    //     if (expiry < currentDate) return true;
    //     else return false;
    // };
    const quoteExpired = (expiryDate: any) => {
        const expiry = new Date(expiryDate);
        const currentDate = new Date();
        expiry.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);

        if (expiry < currentDate) return true;
        else return false;
    };

    isQuoteExpiredFlag = quoteExpired(quoteDetails?.properties["expiryDate"]);
    const disablingAcceptCheckout = () => {
        setIsAcceptCheckoutDisable(true);
    };

    const branchUrl = "/myaccount/shopmybranch";
    return (
        <>
            <StyledWrapper {...styles.adiLogo}>
                <img
                    alt="ADI a Resideo company"
                    src="data:image/svg+xml;base64,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"
                />
            </StyledWrapper>
            <LoadingOverlay
                {...styles.loadingOverlay}
                loading={quoteDetailsLoading}
                spinnerProps={{ color: "primary" }}
            >
                {!quoteDetailsLoading && (
                    <GridContainer {...styles.container}>
                        <GridItem {...styles.headerGridItem}>
                            <ADIRfqQuoteDetailsTitleHeader quoteDetails={newQuoteDetails} />
                            <ADIXtenExportToXtenButton
                                id={quoteNumber + "_quotes_" + customerSuffix}
                                type="myquote"
                                buttonText=""
                                isQuoteExpired={isQuoteExpiredFlag}
                            />
                        </GridItem>

                        {!(returnCode !== null && returnCode !== "00" && returnCode !== "") &&
                            !(orderLinesLength === 0) && (
                                <GridItem {...styles.firstSection}>
                                    <ADIRfqQuoteDetailsHeaderSection
                                        canUserAccess={canUserAccess}
                                        canUserAcceptAndCheckout={canUserAcceptAndCheckout}
                                        quoteDetails={newQuoteDetails}
                                        formattedDate={formattedDate}
                                        formattedExpDate={expDate} // P21 Change: based on respnse parameter
                                        isNonReturnableModalOpen={isNonReturnableModalOpen}
                                        closeModalNonReturnable={closeModalNonReturnable}
                                        acceptCheckout={acceptCheckout}
                                        isCartNotificationModalOpen={isCartNotificationModalOpen}
                                        closeModalCartNotification={closeModalCartNotification}
                                        isLogicBrokerModalOpen={isLogicBrokerModalOpen}
                                        closeModalLogicBroker={closeModalLogicBroker}
                                        checkNonReturnableProducts={checkNonReturnableProducts}
                                        AgreeAndContinueCheckout={AgreeAndContinueCheckout}
                                        isAcceptCheckoutDisable={isAcceptCheckoutDisable}
                                        isExpireQuoteWarning={isExpireQuoteError}
                                        isQuoteExpired={quoteExpired}
                                    />
                                    <ADIRfqQuoteDetailsSummaryTableMobile
                                        quoteDetails={newQuoteDetails}
                                        formattedDate={formattedDate}
                                        formattedExpDate={expDate} // P21 Change, based on response parameter.
                                        isExpireQuoteWarning={isExpireQuoteError}
                                        isQuoteExpired={quoteExpired}
                                    />
                                </GridItem>
                            )}

                        {/* Error messages for expired quote  */}

                        {quoteDetails?.properties["isZeroPriceQuote"] === "N" &&
                            !(orderLinesLength === 0) &&
                            quoteExpired(quoteDetails?.properties["expiryDate"]) && (
                                <GridItem {...styles.erroMsgSection}>
                                    <StyledWrapper className="rd-myquotedetails_maxwd1474">
                                        <StyledWrapper className="error-msg quote-record">
                                            <img
                                                alt="Error Icon"
                                                src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Quotelist/icon-warning.svg"
                                            />
                                            <span>
                                                {translate(
                                                    "Your quote has expired. To renew your quote, please contact your local branch",
                                                )}
                                                :
                                                <a title="Link goes here" href={`${branchUrl}`}>
                                                    {shopmyBranchDetailsLabel} {translate("Branch")}
                                                </a>
                                            </span>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </GridItem>
                            )}

                        {quoteDetails?.properties["isZeroPriceQuote"] === "N" &&
                            !(orderLinesLength === 0) &&
                            isExpireQuoteError(quoteDetails?.properties["expiryDate"]) &&
                            ExpireQuoteDays > 1 && (
                                <GridItem {...styles.erroMsgSection}>
                                    <StyledWrapper className="rd-myquotedetails_maxwd1474">
                                        <StyledWrapper className="error-msg quote-record">
                                            <img
                                                alt="Error Icon"
                                                src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Quotelist/icon-warning.svg"
                                            />
                                            <span>
                                                {translate("Your quote expires in ")}
                                                {ExpireQuoteDays}{" "}
                                                {translate(
                                                    "days. To renew your quote, please contact your local branch,",
                                                )}
                                                :
                                                <a title="Link goes here" href={`${branchUrl}`}>
                                                    {shopmyBranchDetailsLabel} {translate("Branch")}
                                                </a>
                                            </span>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </GridItem>
                            )}

                        {quoteDetails?.properties["isZeroPriceQuote"] === "N" &&
                            !(orderLinesLength === 0) &&
                            isExpireQuoteError(quoteDetails?.properties["expiryDate"]) &&
                            ExpireQuoteDays == 1 && (
                                <GridItem {...styles.erroMsgSection}>
                                    <StyledWrapper className="rd-myquotedetails_maxwd1474">
                                        <StyledWrapper className="error-msg quote-record">
                                            <img
                                                alt="Error Icon"
                                                src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Quotelist/icon-warning.svg"
                                            />
                                            <span>
                                                {translate("Your quote expires in")}&nbsp;
                                                {ExpireQuoteDays}{" "}
                                                {translate(
                                                    " day. To renew your quote, please contact your local branch",
                                                )}
                                                :
                                                <a title="Link goes here" href={`${branchUrl}`}>
                                                    {shopmyBranchDetailsLabel} {translate("Branch")}
                                                </a>
                                            </span>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </GridItem>
                            )}

                        {quoteDetails?.properties["isZeroPriceQuote"] === "N" &&
                            !(orderLinesLength === 0) &&
                            isExpireQuoteError(quoteDetails?.properties["expiryDate"]) &&
                            ExpireQuoteDays == 0 && (
                                <GridItem {...styles.erroMsgSection}>
                                    <StyledWrapper className="rd-myquotedetails_maxwd1474">
                                        <StyledWrapper className="error-msg quote-record">
                                            <img
                                                alt="Error Icon"
                                                src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Quotelist/icon-warning.svg"
                                            />
                                            <span>
                                                {translate(
                                                    "Your quote expires today. To renew your quote, please contact your local branch",
                                                )}
                                                :
                                                <a title="Link goes here" href={`${branchUrl}`}>
                                                    {shopmyBranchDetailsLabel} {translate("Branch")}
                                                </a>
                                            </span>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </GridItem>
                            )}

                        {/* Error messages end for expired quote */}
                        {quoteDetails?.properties["isZeroPriceQuote"] === "Y" && !(orderLinesLength === 0) && (
                            <GridItem {...styles.erroMsgSection}>
                                <StyledWrapper className="rd-myquotedetails_maxwd1474">
                                    <StyledWrapper className="error-msg quote-record">
                                        <img
                                            alt="Error Icon"
                                            src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Quotelist/icon-warning.svg"
                                        />
                                        <span>
                                            {translate(
                                                "There is a problem with your quote. Please contact your local branch",
                                            )}
                                            :
                                            <a title="Link goes here" href={`${branchUrl}`}>
                                                {shopmyBranchDetailsLabel} {translate("Branch")}
                                            </a>
                                        </span>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </GridItem>
                        )}
                        {quoteDetails?.invoiceOrderLines.length > 0 &&
                            !(returnCode !== null && returnCode !== "00" && returnCode !== "") &&
                            !(orderLinesLength === 0) && (
                                <GridItem {...styles.secondSection}>
                                    <ADIRfqQuoteDetailsOrderLinesList
                                        qtyItems={qtyItems}
                                        setQtyItems={setQtyItems}
                                        quoteDetails={newQuoteDetails}
                                        setNewQuoteDetails={setNewQuoteDetails}
                                        disablingAcceptCheckout={disablingAcceptCheckout}
                                        isAcceptCheckoutDisable={isAcceptCheckoutDisable}
                                        setIsAcceptCheckoutDisable={setIsAcceptCheckoutDisable}
                                    />
                                </GridItem>
                            )}

                        {quoteDetails?.invoiceOrderLines?.length > 0 && (
                            <GridItem {...styles.thirdSection}>
                                <ADIRfqQuoteDetailsBottomTotalSection
                                    formattedExpDate={expDate} // P21 change, based on response parameter.
                                    canUserAccess={canUserAccess}
                                    canUserAcceptAndCheckout={canUserAcceptAndCheckout}
                                    quoteDetails={newQuoteDetails}
                                    isNonReturnableModalOpen={isNonReturnableModalOpen}
                                    closeModalNonReturnable={closeModalNonReturnable}
                                    acceptCheckout={acceptCheckout}
                                    isCartNotificationModalOpen={isCartNotificationModalOpen}
                                    closeModalCartNotification={closeModalCartNotification}
                                    isLogicBrokerModalOpen={isLogicBrokerModalOpen}
                                    closeModalLogicBroker={closeModalLogicBroker}
                                    checkNonReturnableProducts={checkNonReturnableProducts}
                                    AgreeAndContinueCheckout={AgreeAndContinueCheckout}
                                    continueAndCheckoutFromLB={continueAndCheckoutFromLB}
                                    isAcceptCheckoutDisable={isAcceptCheckoutDisable}
                                    isQuoteExpired={isQuoteExpired}
                                />
                            </GridItem>
                        )}
                        {(returnCode !== null && returnCode !== "00" && returnCode !== "") ||
                            (orderLinesLength === 0 && (
                                <GridItem {...styles.erroMsgSection}>
                                    <StyledWrapper className="rd-myquotedetails_maxwd1474">
                                        <StyledWrapper className="error-msg quote-record">
                                            <img
                                                alt="Error Icon"
                                                src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Quotelist/icon-warning.svg"
                                            />
                                            <StyledWrapper>{translate("Quote record not available")}</StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </GridItem>
                            ))}
                    </GridContainer>
                )}
            </LoadingOverlay>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(ADIQuoteDetailsContainer)),
    definition: {
        group: "RFQ Quote Details",
        allowedContexts: [RfqQuoteDetailsPageContext],
        displayName: "ADI Quote Details Container",
    },
};

export default widgetModule;
